import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";
import React, { useCallback, useEffect, useState } from "react";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import ShopifyAccounts from "./ShopifyAccounts";
import ShopifyBotKnowledge from "./ShopifyBotKnowledge";
import ShopifyDataMapping from "./ShopifyDataMapping";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import { Link, useNavigate } from "react-router-dom";
import { IconClose1 } from "@velaro/velaro-component-lib";
import IntegrationActivityLog from "../IntegrationActivityLog";
import SkillControlsPanel from "../SkillControlsPanel";
import ShopperProfileTab from "../shared/ShopperProfileTab";
import useAdminFeatures from "@/hooks/useAdminFeatures";

interface ShopifyConfig {
  id: number;
  storeName: string;
  appClientId: string;
  appSecret: string;
  domain: string;
  teamId: number;
  widgetDeployed?: boolean;
}

interface ShopifyStatus {
  isConnected: boolean;
  hasToken?: boolean;
  storeName?: string;
  domain?: string;
}

// ─── AI Tools ──────────────────────────────────────────────────────────────────

const AI_TOOLS = [
  {
    name: "shopify_get_customer",
    label: "Look up customer",
    description: "Finds a customer by email or name and returns their account details, lifetime value, and order count.",
    available: true
  },
  {
    name: "shopify_search_products",
    label: "Search products",
    description: "Searches your product catalog by keyword and returns matching items with prices, availability, and URLs.",
    available: true
  },
  {
    name: "shopify_get_order",
    label: "Get order details",
    description: "Fetches a specific order by ID — line items, totals, fulfillment status, and tracking information.",
    available: true
  },
  {
    name: "shopify_get_recent_orders",
    label: "Recent orders for customer",
    description: "Returns the last 5 orders for a customer so the AI can answer 'where is my order?' without asking for an order number.",
    available: true
  },
  {
    name: "shopify_get_abandoned_cart",
    label: "Abandoned cart recovery",
    description: "Finds incomplete checkouts by email and returns a recovery link — bot sends it directly in chat to recover lost sales.",
    available: true
  },
  {
    name: "shopify_apply_coupon",
    label: "Coupon code validation",
    description: "Validates a discount code in real time — shows discount value and expiry, then shares the code with checkout instructions.",
    available: true
  },
  {
    name: "shopify_check_stock",
    label: "Live stock levels",
    description: "Checks real-time inventory — uses urgency language when stock is low ('Only 3 left!') to drive purchase decisions.",
    available: true
  },
  {
    name: "shopify_create_quote",
    label: "Quote builder / checkout link",
    description: "Creates a Shopify draft order from the conversation and sends a direct checkout link — closes sales in chat without an agent.",
    available: true
  },
  {
    name: "shopify_initiate_return",
    label: "Self-service returns",
    description: "Submits a return request directly in Shopify — customer completes the return in chat, no email or form needed.",
    available: true
  },
  {
    name: "shopify_get_tracking",
    label: "Order tracking",
    description: "Fetches live tracking info and carrier link for any order number — answers 'where is my package?' instantly.",
    available: true
  },
  {
    name: "shopify_get_reviews",
    label: "Product reviews (Judge.me)",
    description: "Fetches product reviews from Judge.me — bot shows star ratings and review highlights when customers ask 'is this product good?'",
    available: true
  },
  {
    name: "shopify_get_cart",
    label: "View active cart",
    description: "Retrieves cart contents, item count, subtotal, and applied discounts — lets the AI reference what a visitor has selected before checkout.",
    available: true
  },
  {
    name: "shopify_get_product_details",
    label: "Full product details",
    description: "Full product details by ID — variants, images, pricing, inventory.",
    available: true
  },
  {
    name: "shopify_get_shop_info",
    label: "Store info",
    description: "Store name, domain, currency, plan, timezone, and country.",
    available: true
  },
  {
    name: "shopify_cancel_order",
    label: "Cancel order",
    description: "Cancel a Shopify order. Always confirm with customer first.",
    available: true
  },
  {
    name: "shopify_get_refunds",
    label: "Get refunds",
    description: "List all refunds for an order with amounts and notes.",
    available: true
  },
  {
    name: "shopify_create_refund",
    label: "Create refund",
    description: "Process a partial or full refund on an order.",
    available: true
  },
  {
    name: "shopify_get_collections",
    label: "Product collections",
    description: "Product collections with handle, description, and product count.",
    available: true
  },
  {
    name: "shopify_get_product_variants",
    label: "Product variants",
    description: "All sizes, colors, and options with per-variant pricing and stock.",
    available: true
  },
  {
    name: "shopify_search_orders",
    label: "Search orders",
    description: "Search orders by customer name, email, or order number.",
    available: true
  },
  {
    name: "shopify_add_order_note",
    label: "Add order note",
    description: "Add an internal note to an order for tracking.",
    available: true
  },
  {
    name: "shopify_get_order_notes",
    label: "Order notes",
    description: "Order communication history and timeline events.",
    available: true
  },
  {
    name: "shopify_get_shipping_zones",
    label: "Shipping zones",
    description: "Shipping zones with countries and rate names/prices.",
    available: true
  },
  {
    name: "shopify_get_discount_codes",
    label: "Discount codes",
    description: "Active discount codes with type, value, and expiry.",
    available: true
  },
  {
    name: "shopify_compare_products",
    label: "Compare products",
    available: true,
    description: "Compare two Shopify products side by side — prices, stock, variants, ratings, and key differences. Triggered by 'what's the difference between X and Y?'"
  },
  {
    name: "shopify_recover_cart",
    label: "Recover abandoned cart",
    available: true,
    description: "Proactively recover abandoned checkouts — fetch incomplete cart, build recovery URL, optionally apply a discount code, and generate a personalized recovery message."
  },
  {
    name: "shopify_get_customer_insights",
    label: "Customer intelligence",
    available: true,
    description: "Deep customer profile — lifetime value, VIP status, purchase frequency, average order value, top products, and recommended engagement approach."
  },
  {
    name: "shopify_get_recommendations",
    label: "Product recommendations",
    available: true,
    description: "Returns up to 5 related products from the same Shopify collection. Triggers on 'what else goes with X?', 'show me similar products', or proactive upselling after showing an item."
  },
  {
    name: "shopify_create_discount",
    label: "Create discount code",
    available: true,
    description: "Creates a real-time promo code (percentage, fixed amount, or free shipping) with configurable usage limit and expiry. Use for cart recovery offers, VIP rewards, or price-match situations."
  },
  {
    name: "shopify_get_store_policies",
    label: "Store policies",
    available: true,
    description: "Fetches the store's live return/refund, shipping, privacy, and terms of service policies directly from Shopify. AI summarizes the relevant policy when a customer asks — always accurate, never outdated."
  },
  {
    name: "shopify_get_metafields",
    label: "Get custom metafields",
    available: true,
    description: "Fetch custom metafields for a Shopify product or order by GID. Returns namespace, key, value, and type — lets the AI surface merchant-defined attributes like fit guides, certifications, or care instructions."
  },
  {
    name: "shopify_get_blog_articles",
    label: "Search blog articles",
    available: true,
    description: "Search Shopify blog articles by keyword. Returns title, summary, author, and publish date — lets the AI answer 'do you have a guide on X?' from your own store content."
  },
  {
    name: "shopify_create_fulfillment",
    label: "Mark order shipped",
    available: true,
    description: "Fulfill a Shopify order — mark it as shipped with tracking info and optionally notify the customer. Use when an agent confirms shipment during a live chat."
  },
  {
    name: "shopify_get_draft_orders",
    label: "Get draft orders",
    available: true,
    description: "Look up Shopify draft orders (quotes) by customer email or status. Returns line items and totals — useful for following up on B2B quotes or incomplete sales."
  },
  {
    name: "shopify_update_order_tags",
    label: "Tag order",
    available: true,
    description: "Add tags to a Shopify order for internal categorization — priority, VIP, support channel, review flags, etc. Helps teams filter and report on chat-originated orders."
  }
];

const COMING_SOON_TOOLS: { name: string; label: string; description: string }[] = [];

// ─── Capabilities Panel ────────────────────────────────────────────────────────

const CAPABILITIES = [
  {
    title: "Customer lookup",
    icon: "🛍️",
    items: [
      "Find any customer instantly by email or name — account details, lifetime value, total order count",
      "Returns full account info including order history so the AI can answer 'what have I bought?' automatically",
      "Identifies returning vs. new customers so the AI can personalise its greeting and recommendations",
      "Works in real time during the chat — no manual CRM lookup needed by the agent",
      "Get deep customer intelligence: lifetime value, VIP status, order frequency, and recommended approach",
      "Proactively recover abandoned carts with personalized messages and optional discount codes"
    ]
  },
  {
    title: "Order management",
    icon: "📦",
    items: [
      "Look up any order by ID — line items, quantities, pricing, shipping address, and tracking number",
      "Find all recent orders for a customer's email — for 'where is my order?' without needing the order number",
      "Returns fulfillment status: unfulfilled / partially fulfilled / fulfilled / refunded",
      "AI reads live Shopify data at query time — no sync lag, no stale data"
    ]
  },
  {
    title: "Product search",
    icon: "🏷️",
    items: [
      "Search your Shopify product catalog by keyword — returns name, price, inventory level, and product URL",
      "AI can answer pre-sales questions about availability, variants, and pricing from live catalog data",
      "Out-of-stock handling: AI can suggest alternatives automatically using a second product search",
      "Product URLs included — AI can share direct buy links in the conversation",
      "Compare two products side by side — price, stock, variants, and ratings (triggered by 'what's the difference between X and Y?')"
    ]
  },
  {
    title: "Cart & checkout",
    icon: "🛒",
    items: [
      "shopify_get_cart: AI sees current cart contents, subtotal, and applied discounts before checkout",
      "shopify_get_abandoned_checkouts: Identify visitors who left mid-checkout — enables AI-driven cart recovery",
      "shopify_apply_discount: AI applies a discount code to the cart during the conversation to close a sale",
      "shopify_create_draft_order: AI creates a draft order for custom quotes or agent-assisted purchases"
    ]
  },
  {
    title: "AI workflow tools",
    icon: "🤖",
    items: [
      "30 AI tools: customer lookup, product search, compare products, product reviews, product recommendations, order tracking, returns, quote builder, abandoned cart recovery, coupon validation, stock levels, order history, recent orders, cart viewer, full product details, store info, cancel order, refunds, create refund, collections, product variants, search orders, order notes, add order note, shipping zones, discount codes, recover abandoned cart, customer intelligence, create discount code, store policies",
      "AI decides when to call each tool based on the conversation — no configuration required",
      "Tools are injected automatically when your Shopify store is connected via OAuth",
      "Field mapping is optional — the AI works immediately; mapping adds agent sidebar and HTTP POST variables"
    ]
  }
];

// ─── How it all works ─────────────────────────────────────────────────────────

function HowItWorksCard() {
  return (
    <div className="bg-white rounded-2xl shadow-md p-6 mb-4">
      <div className="font-semibold text-slate-800 text-base mb-1">How Shopify works in Velaro</div>
      <p className="text-xs text-slate-500 mb-4">Three steps — connect once, everything else is automatic.</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Step 1 */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-slate-800 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
            <span className="font-semibold text-slate-700 text-sm">Connect via OAuth</span>
          </div>
          <p className="text-xs text-slate-500 mb-3">Enter your store URL and click Connect — Velaro handles the OAuth handshake with Shopify automatically. No credentials to manage.</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span className="font-mono bg-white border border-slate-200 px-1.5 py-0.5 rounded text-[10px]">yourstore.myshopify.com</span>
              <span className="text-slate-400">enter URL → authorize</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span className="font-mono bg-white border border-slate-200 px-1.5 py-0.5 rounded text-[10px]">Access token</span>
              <span className="text-slate-400">stored securely, auto-refreshed</span>
            </div>
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-slate-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
              <path d="M3 2L7 5L3 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        </div>

        {/* Step 2 */}
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
            <span className="font-semibold text-green-800 text-sm">AI skills go live</span>
          </div>
          <p className="text-xs text-green-700 mb-3">30 tools activate immediately — the AI calls them based on what visitors ask. No configuration needed.</p>
          <div className="grid grid-cols-2 gap-1">
            {["Customer lookup","Product search","Order details","Recent orders","Abandoned cart","Coupon validate","Live stock","Quote builder","Self-service returns","Order tracking","Product reviews","Cart viewer","Product details","Store info","Cancel order","Refunds","Create refund","Collections","Product variants","Search orders","Order notes","Add order note","Shipping zones","Discount codes","Compare products","Recover abandoned cart","Customer intelligence"].map((t) => (
              <div key={t} className="flex items-center gap-1 text-[10px] text-green-700">
                <svg viewBox="0 0 8 8" width="8" height="8" fill="none" className="flex-shrink-0">
                  <path d="M1 4L3 6L7 2" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {t}
              </div>
            ))}
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-slate-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
              <path d="M3 2L7 5L3 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        </div>

        {/* Step 3 */}
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-[var(--color-primary)] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
            <span className="font-semibold text-blue-800 text-sm">Data surfaces everywhere</span>
          </div>
          <p className="text-xs text-blue-700 mb-3">Data appears in 3 places automatically — no configuration.</p>
          <div className="space-y-2">
            <div className="bg-white rounded-lg p-2 border border-blue-100">
              <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">Agent sidebar</div>
              <div className="text-[10px] text-blue-600 mt-0.5">Shopify tab → Customer profile, order list, and product info panels during live chat</div>
            </div>
            <div className="bg-white rounded-lg p-2 border border-blue-100">
              <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">Contact tab strip</div>
              <div className="text-[10px] text-blue-600 mt-0.5">Tags, Lifetime Value, and Order Count shown at a glance on the main info panel</div>
            </div>
            <div className="bg-white rounded-lg p-2 border border-blue-100">
              <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">HTTP POST variables</div>
              <div className="text-[10px] text-blue-600 mt-0.5">Field Reference tab → copy variable names to push Shopify data to your CRM or ticketing system</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Capabilities panel ───────────────────────────────────────────────────────

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0]));

  function toggleSection(i: number) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  }

  return (
    <div className="bg-white rounded-2xl shadow-md mb-4">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 rounded-2xl"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-slate-800">What's included</span>
          <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">
            {CAPABILITIES.length} feature areas
          </span>
        </div>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 16 16" fill="currentColor"
        >
          <path d="M8 10.5L3 5.5H13L8 10.5Z" />
        </svg>
      </button>

      {open && (
        <div className="border-t border-slate-100 px-6 pb-6">
          {/* How AI tools work — no mapping required */}
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 p-3 mb-4 text-xs text-green-800">
            <strong>AI tools work without any configuration.</strong> Connect your Shopify store via OAuth
            and the AI can immediately call customer, order, and product lookup tools during any conversation.
            No JSON, no field mapping, no webhook setup required — the AI orchestrates tool calls automatically
            based on what the visitor asks.
          </div>

          <div className="space-y-2">
            {CAPABILITIES.map((cap, i) => (
              <div key={i} className="rounded-lg border border-slate-100 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection(i)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
                  aria-expanded={openSections.has(i)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-base">{cap.icon}</span>
                    <span className="text-sm font-medium text-slate-700">{cap.title}</span>
                  </div>
                  <svg
                    className={`w-3.5 h-3.5 text-slate-400 transition-transform flex-shrink-0 ${openSections.has(i) ? "rotate-180" : ""}`}
                    viewBox="0 0 16 16" fill="currentColor"
                  >
                    <path d="M8 10.5L3 5.5H13L8 10.5Z" />
                  </svg>
                </button>
                {openSections.has(i) && (
                  <div className="px-4 pb-3 space-y-1.5 border-t border-slate-50">
                    {cap.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-2 pt-1.5">
                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 bg-green-500" />
                        <span className="text-xs text-slate-600">{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* How it fits orchestration */}
          <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 p-4 text-xs text-blue-800">
            <div className="font-semibold mb-1">How orchestration works</div>
            <p className="mb-2">
              Shopify tools are automatically available to any AI workflow node on your site. The AI calls them as needed based on the conversation — you don't need to configure which tools to call.
            </p>
            <p>
              For advanced setups: use the <strong>Store Specialist (Orchestration)</strong> template in the Starter Wizard to create a dedicated Shopify AI that a main routing AI can delegate to.
              Field mapping and HTTP POST nodes are optional extras — useful for pushing data to external systems after the AI handles the conversation.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Deploy Widget Section ─────────────────────────────────────────────────────

function DeployWidgetCallout({ widgetDeployed }: { widgetDeployed: boolean }) {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-medium text-blue-800 mb-0.5">
            {widgetDeployed ? "✓ Widget deployed to this store" : "Deploy the chat widget to your store"}
          </div>
          <div className="text-xs text-blue-600">
            Configure your chat window styling, then grab the embed code from Deployments to add it to your store.
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/chat_window"
            className="text-xs px-3 py-1.5 rounded-lg bg-white border border-blue-300 text-blue-700 hover:bg-blue-100 transition"
          >
            Chat Window
          </Link>
          <Link
            to="/deployments"
            className="text-xs px-3 py-1.5 rounded-lg bg-[var(--color-primary)] hover:opacity-90 text-white transition"
          >
            Get Embed Code →
          </Link>
        </div>
      </div>
      <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
        <i className="fa-solid fa-lightbulb mt-0.5 shrink-0" />
        <span>
          <strong>Tip:</strong> You can also create a <strong>Knowledge Base Q&A</strong> page widget
          from <Link to="/deployments" className="underline hover:text-blue-900">Deployments → Page Widgets</Link> for
          a lightweight, AI-only Q&A experience — no live agent needed. Great as a standalone product.
        </span>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Shopify() {
  const api = useApi();
  const [configs, setConfigs] = useState<ShopifyConfig[]>([]);
  const { teamId } = useSelectedTeam();
  const [selectedConfigId, setSelectedConfigId] = useState<number>(0);
  const [aiStatus, setAiStatus] = useState<ShopifyStatus | null>(null);
  const { enableEcommerceSync } = useAdminFeatures();
  const [activeTab, setActiveTab] = useState<"setup" | "bot-knowledge" | "data-mapping" | "shoppers" | "logs">("setup");
  const [showWizard, setShowWizard] = useState(false);
  const navigate = useNavigate();

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.messaging.get(`Shopify/Integrations/`);
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
      if (Array.isArray(data) && data.length === 1) {
        setSelectedConfigId(data[0].id);
      }
    } catch {
      setConfigs([]);
    }
  }, [api.messaging]);

  const addConfig = useCallback(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    if (!selectedConfigId) {
      setAiStatus(null);
      return;
    }
    const domain = configs.find((c) => c.id === selectedConfigId)?.domain;
    if (!domain) return;
    async function fetchStatus() {
      try {
        const resp = await api.messaging.get(`Shopify/status/${domain}`);
        if (resp.ok) {
          const data = await resp.json();
          setAiStatus(data);
        }
      } catch {
        // non-fatal
      }
    }
    fetchStatus();
  }, [api.messaging, selectedConfigId, configs]);

  const deleteConfig = useCallback(
    async (id: number) => {
      setConfigs((oldState) => oldState.filter((x) => x.id !== id));
      await api.messaging.delete(`Shopify/Integrations/${id}`);
    },
    [api.messaging]
  );

  const handleConfigChange = (
    id: number,
    field: keyof ShopifyConfig,
    value: string
  ) => {
    setConfigs((prevConfigs) =>
      prevConfigs.map((config) =>
        config.id === id ? { ...config, [field]: value } : config
      )
    );
  };

  const aiActive = aiStatus?.isConnected === true || aiStatus?.hasToken === true;

  return (
    <div>
      {showWizard && (
        <StarterWorkflowWizard
          integration="shopify"
          shopifyConfigId={selectedConfigId || undefined}
          websiteUrl={configs.find((c) => c.id === selectedConfigId)?.domain
            ? `https://${configs.find((c) => c.id === selectedConfigId)!.domain}`
            : undefined}
          onClose={() => setShowWizard(false)}
        />
      )}

      {/* Tab navigation */}
      <div className="flex border-b border-slate-200 mb-4">
        {(["setup", "bot-knowledge", "data-mapping", "logs"] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            className={`py-2 mr-4 text-sm cursor-pointer ${activeTab === tab ? "border-b-2 border-primary text-primary font-medium" : "text-slate-500 font-normal"}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "setup"
              ? "Setup"
              : tab === "bot-knowledge"
              ? "Bot Knowledge"
              : tab === "data-mapping"
              ? "Field Reference"
              : "Activity Log"}
          </button>
        ))}
        {enableEcommerceSync && (
          <button
            type="button"
            className={`py-2 mr-4 text-sm cursor-pointer ${activeTab === "shoppers" ? "border-b-2 border-primary text-primary font-medium" : "text-slate-500 font-normal"}`}
            onClick={() => setActiveTab("shoppers")}
          >
            Shoppers
          </button>
        )}
      </div>

      {activeTab === "shoppers" && enableEcommerceSync && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="font-semibold text-slate-800 text-lg mb-1">Shopper Profiles — Shopify</div>
          <p className="text-sm text-slate-500 mb-4">
            All customers synced from your connected Shopify store. Data updates hourly.
            Use this to segment campaigns by purchase behavior, lifetime value, and cart abandonment.
          </p>
          <ShopperProfileTab platform="Shopify" />
        </div>
      )}

      {activeTab === "logs" && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <IntegrationActivityLog integrationSlug="Shopify" />
        </div>
      )}

      {activeTab === "bot-knowledge" && (
        <ShopifyBotKnowledge
          shopifyDomain={configs.find((c) => c.id === selectedConfigId)?.domain}
        />
      )}

      {activeTab === "data-mapping" && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="font-semibold text-slate-800 text-lg mb-1">Field Reference — Shopify</div>
          <p className="text-sm text-slate-500 mb-3">
            All standard Shopify fields available in every connected store. Use these variable names
            in HTTP POST nodes to forward Shopify data to your CRM, ticketing system, or any webhook.
            The agent sidebar and at-a-glance strip display customer and order data automatically — no configuration needed.
          </p>
          <div className="mb-4 rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-800">
            <strong>Already works out of the box.</strong> The agent sidebar, at-a-glance strip, and AI tools
            all pull Shopify data automatically as soon as your store is connected. This reference is here
            if you want to forward specific fields to another system using HTTP POST workflow nodes.
          </div>
          <ShopifyDataMapping
            teamId={teamId ?? 0}
            selectedConfigId={selectedConfigId}
          />
        </div>
      )}

      {activeTab === "setup" && (
        <div>
          {/* Architecture overview — always visible */}
          <HowItWorksCard />
          {/* Capabilities panel — always visible */}
          <CapabilitiesPanel />

          <div className="mb-6">
            <ShopifyAccounts
              configs={configs}
              deleteConfig={deleteConfig}
              addConfig={addConfig}
              teamId={teamId!}
              onChange={handleConfigChange}
              onSelectConfig={setSelectedConfigId}
            />
          </div>

          {/* Deploy Widget */}
          {selectedConfigId > 0 && (
            <DeployWidgetCallout
              widgetDeployed={configs.find((c) => c.id === selectedConfigId)?.widgetDeployed ?? false}
            />
          )}

          {/* AI Tools panel */}
          {selectedConfigId > 0 && (
            <div className="bg-white rounded-2xl shadow-md p-6 mb-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-semibold text-slate-800 text-lg flex items-center gap-2">
                    AI Tools
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        aiActive
                          ? "bg-green-100 text-green-700"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {aiActive ? "Active" : aiStatus === null ? "Checking…" : "No token"}
                    </span>
                  </div>
                  <div className="text-sm text-slate-500 mt-0.5">
                    {aiActive
                      ? "These tools are available to any AI workflow node on this site. The AI calls them automatically — no configuration required."
                      : "Connect your Shopify store via OAuth to activate AI tools. Once connected, any AI workflow node can query your store in real time."}
                  </div>
                </div>
                {aiActive && (
                  <button
                    type="button"
                    onClick={() => setShowWizard(true)}
                    className="text-sm px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 whitespace-nowrap font-medium shadow-sm"
                  >
                    Launch Starter Wizard
                  </button>
                )}
              </div>

              <div className="mt-8">
                <SkillControlsPanel integration="shopify" skills={AI_TOOLS} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function BackToIntegrationLanding() {
  const navigate = useNavigate();
  return (
    <div
      className="flex cursor-pointer"
      onClick={() => navigate("/integrations")}
    >
      <IconClose1 className="h-6 w-6" />
    </div>
  );
}
